<template>
  <div>
    <v-skeleton-loader
      v-if="isLoading"
      type="table"
    />

    <div
      v-else
      class="pb-15"
    >
      <!-- ヘッダー -->
      <div class="d-flex align-center justify-space-between mb-4">
        <h1 class="text-h5">
          在庫管理
        </h1>
        <div>
          <v-btn
            color="secondary"
            class="mr-2"
            @click="showTagTrackingDialog = true"
          >
            タグ一括対象化
          </v-btn>
          <v-btn
            color="primary"
            @click="openCreateDialog"
          >
            ＋対象にする
          </v-btn>
        </div>
      </div>

      <!-- フィルター -->
      <v-card class="mb-4">
        <v-card-text>
          <v-row align="center">
            <v-col
              cols="12"
              sm="4"
            >
              <v-select
                v-model="selectedTagId"
                :items="tagOptions"
                item-text="name"
                item-value="id"
                label="商品タグ"
                clearable
                dense
                outlined
                hide-details
              />
            </v-col>
            <v-col
              cols="12"
              sm="4"
            >
              <v-text-field
                v-model="searchQuery"
                :prepend-inner-icon="icons.mdiMagnify"
                label="商品名で検索"
                clearable
                dense
                outlined
                hide-details
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- テーブル -->
      <v-card>
        <v-card-text>
          <div
            v-if="selectedItems.length > 0"
            class="d-flex justify-end mb-2"
          >
            <v-btn
              color="error"
              text
              @click="confirmBulkDelete"
            >
              選択したものを対象から外す ({{ selectedItems.length }})
            </v-btn>
          </div>

          <v-data-table
            v-model="selectedItems"
            :headers="headers"
            :items="filteredItems"
            :items-per-page="-1"
            hide-default-footer
            show-select
            no-data-text="在庫管理対象のアイテムがありません"
          >
            <!-- 管理方式 -->
            <template #[`item.unitType`]="{ item }">
              <v-chip
                small
                :color="item.unitType === 'piece' ? 'primary' : 'secondary'"
                text-color="white"
              >
                {{ item.unitType === 'piece' ? '個数' : '杯数' }}
              </v-chip>
            </template>

            <!-- タグ -->
            <template #[`item.vendibleTagNames`]="{ item }">
              <template v-if="item.vendibleTagNames && item.vendibleTagNames.length">
                <v-chip
                  v-for="(tag, index) in (Array.isArray(item.vendibleTagNames) ? item.vendibleTagNames : item.vendibleTagNames.split(', '))"
                  :key="index"
                  x-small
                  class="mr-1 mb-1"
                >
                  {{ tag }}
                </v-chip>
              </template>
              <span v-else>-</span>
            </template>

            <!-- 在庫数 -->
            <template #[`item.currentQuantity`]="{ item }">
              {{ formatCurrentQuantity(item) }}
            </template>

            <!-- 状態 -->
            <template #[`item.stockStatus`]="{ item }">
              <v-chip
                small
                :color="getStatusColor(item)"
                :class="getStatusTextClass(item)"
              >
                {{ getStatusLabel(item) }}
              </v-chip>
            </template>

            <!-- 最終補充日 -->
            <template #[`item.lastReplenishedAt`]="{ item }">
              {{ formatDate(item.lastReplenishedAt) }}
            </template>

            <!-- 最終減算日 -->
            <template #[`item.lastDeductedAt`]="{ item }">
              {{ formatDate(item.lastDeductedAt) }}
            </template>

            <!-- 操作 -->
            <template #[`item.actions`]="{ item }">
              <v-menu
                offset-y
                left
              >
                <template #activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small>
                      {{ icons.mdiDotsVertical }}
                    </v-icon>
                  </v-btn>
                </template>
                <v-list
                  dense
                  class="py-2"
                >
                  <v-list-item
                    class="px-6"
                    @click="openEditDialog(item)"
                  >
                    <v-list-item-title>管理設定</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    class="px-6"
                    @click="openReplenishmentDialog(item)"
                  >
                    <v-list-item-title>補充</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    class="px-6"
                    @click="openAdjustmentDialog(item)"
                  >
                    <v-list-item-title>調整</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    class="px-6"
                    @click="confirmDelete(item)"
                  >
                    <v-list-item-title class="error--text">
                      対象から外す
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- ダイアログ -->
      <inventory-form-dialog
        v-model="showFormDialog"
        :edit-item="editingItem"
        :items="availableItems"
        @saved="handleSaved"
      />

      <replenishment-dialog
        v-model="showReplenishmentDialog"
        :inventory-item="targetItem"
        @saved="handleSaved"
      />

      <adjustment-dialog
        v-model="showAdjustmentDialog"
        :inventory-item="targetItem"
        @saved="handleSaved"
      />

      <tag-tracking-dialog
        v-model="showTagTrackingDialog"
        :tags="vendibleTags"
        @saved="handleSaved"
      />

      <!-- 確認ダイアログ -->
      <v-dialog
        v-model="showDeleteDialog"
        max-width="400"
      >
        <v-card>
          <v-card-title>確認</v-card-title>
          <v-card-text>
            {{ deleteMessage }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              @click="showDeleteDialog = false"
            >
              キャンセル
            </v-btn>
            <v-btn
              color="error"
              text
              :loading="isDeleting"
              @click="executeDelete"
            >
              対象から外す
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import {
  ref,
  computed,
  getCurrentInstance,
} from '@vue/composition-api'
import { mdiMagnify, mdiDotsVertical } from '@mdi/js'
import { format, parseISO } from 'date-fns'
import InventoryItemApi from '@/api/v2/inventory/InventoryItem'
import VendibleTagApi from '@/api/waiter/VendibleTag'
import ItemApi from '@/api/waiter/Item'
import InventoryFormDialog from './components/InventoryFormDialog.vue'
import ReplenishmentDialog from './components/ReplenishmentDialog.vue'
import AdjustmentDialog from './components/AdjustmentDialog.vue'
import TagTrackingDialog from './components/TagTrackingDialog.vue'

export default {
  components: {
    InventoryFormDialog,
    ReplenishmentDialog,
    AdjustmentDialog,
    TagTrackingDialog,
  },
  setup() {
    const vm = getCurrentInstance().proxy

    // ローディング状態
    const isLoading = ref(false)
    const isDeleting = ref(false)

    // データ
    const inventoryItems = ref([])
    const vendibleTags = ref([])
    const allItems = ref([])
    const selectedItems = ref([])

    // フィルター
    const selectedTagId = ref(null)
    const searchQuery = ref('')

    // ダイアログ
    const showFormDialog = ref(false)
    const showReplenishmentDialog = ref(false)
    const showAdjustmentDialog = ref(false)
    const showTagTrackingDialog = ref(false)
    const showDeleteDialog = ref(false)

    // 編集・操作対象
    const editingItem = ref(null)
    const targetItem = ref(null)
    const deleteTargetIds = ref([])

    // テーブルヘッダー
    const headers = [
      {
        text: '商品名', value: 'itemName', sortable: true,
      },
      {
        text: '管理方式', value: 'unitType', sortable: true, width: '100px',
      },
      {
        text: 'タグ', value: 'vendibleTagNames', sortable: false,
      },
      {
        text: '現在在庫', value: 'currentQuantity', sortable: true, width: '120px',
      },
      {
        text: '基準在庫', value: 'standardQuantity', sortable: true, width: '100px',
      },
      {
        text: '状態', value: 'stockStatus', sortable: true, width: '80px',
      },
      {
        text: '最終補充日', value: 'lastReplenishedAt', sortable: true, width: '120px',
      },
      {
        text: '最終減算日', value: 'lastDeductedAt', sortable: true, width: '120px',
      },
      {
        text: '操作', value: 'actions', sortable: false, width: '60px',
      },
    ]

    // タグフィルターオプション
    const tagOptions = computed(() => {
      return [{ id: null, name: 'すべて' }, ...vendibleTags.value]
    })

    // フィルター適用後のアイテム（登録順=新しい順でソート）
    const filteredItems = computed(() => {
      let items = inventoryItems.value

      // タグフィルター
      if (selectedTagId.value) {
        const selectedId = Number(selectedTagId.value)
        items = items.filter(item => {
          const tagIds = item.vendibleTagIds || []

          return tagIds.some(id => Number(id) === selectedId)
        })
      }

      // 検索フィルター
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        items = items.filter(item => item.itemName?.toLowerCase().includes(query))
      }

      // 登録順（新しい順）でソート
      return [...items].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0)
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0)

        return dateB - dateA
      })
    })

    // 在庫管理対象外のアイテム（新規登録用）
    const availableItems = computed(() => {
      const trackedItemIds = inventoryItems.value.map(i => i.itemId)

      return allItems.value.filter(item => !trackedItemIds.includes(item.id))
    })

    // 削除メッセージ
    const deleteMessage = computed(() => {
      const count = deleteTargetIds.value.length

      return count === 1
        ? 'このアイテムを在庫管理対象から外しますか？'
        : `${count}件のアイテムを在庫管理対象から外しますか？`
    })

    // ユーティリティ関数
    const formatDate = value => {
      if (!value) return '--'

      return format(parseISO(value), 'yyyy/MM/dd')
    }

    const getStatusColor = item => {
      const current = Number(item.currentQuantity) || 0
      const standard = Number(item.standardQuantity) || 0
      if (current === 0) return 'error lighten-4'
      if (standard && current < standard) return 'warning lighten-4'

      return 'success lighten-4'
    }

    const getStatusTextClass = item => {
      const current = Number(item.currentQuantity) || 0
      const standard = Number(item.standardQuantity) || 0
      if (current === 0) return 'error--text text--darken-2'
      if (standard && current < standard) return 'warning--text text--darken-3'

      return 'success--text text--darken-2'
    }

    const getStatusLabel = item => {
      const current = Number(item.currentQuantity) || 0
      const standard = Number(item.standardQuantity) || 0
      if (current === 0) return '不足'
      if (standard && current < standard) return '警告'

      return '充足'
    }

    // 杯数管理用: 1本あたり杯数を計算
    const calcServingsPerBottle = item => {
      const capacity = Number(item.bottleCapacityMl) || 0
      const serving = Number(item.servingSizeMl) || 0
      if (capacity === 0 || serving === 0) return 0

      return Math.floor(capacity / serving)
    }

    // 杯数管理用: ボトル本数を計算
    const calcBottles = item => {
      const servingsPerBottle = calcServingsPerBottle(item)
      if (servingsPerBottle === 0) return 0

      return Math.floor((Number(item.currentQuantity) || 0) / servingsPerBottle)
    }

    // 現在在庫の表示フォーマット
    const formatCurrentQuantity = item => {
      const current = Number(item.currentQuantity) || 0
      if (item.unitType === 'piece') {
        return current
      }
      // 杯数管理: ○杯（○本）
      const bottles = calcBottles(item)

      return `${current}（${bottles}本）`
    }

    // API呼び出し
    const fetchData = async () => {
      isLoading.value = true

      try {
        const [inventoryRes, tagsRes, itemsRes] = await Promise.all([
          InventoryItemApi.getItems(),
          VendibleTagApi.getVendibleTags(),
          ItemApi.getItems(),
        ])

        // inventory_items: JSONAPI形式 { status, message, data: { data: [...] } }
        const inventoryData = inventoryRes?.data?.data?.data || []
        inventoryItems.value = inventoryData.map(item => {
          const attrs = item.attributes

          return {
            id: item.id,
            ...attrs,
            currentQuantity: Math.floor(Number(attrs.currentQuantity) || 0),
            standardQuantity: attrs.standardQuantity != null ? Math.floor(Number(attrs.standardQuantity)) : null,
          }
        })

        // vendible_tags: { vendibleTags: { data: [...] } }
        const tagsData = tagsRes?.data?.vendibleTags?.data || []
        vendibleTags.value = tagsData.map(item => ({
          id: item.attributes?.id || item.id,
          name: item.attributes?.name,
        }))

        // items: { items: { data: [...] } }
        const itemsData = itemsRes?.data?.items?.data || []
        allItems.value = itemsData.map(item => ({
          id: item.id,
          ...item.attributes,
        }))
      } catch (error) {
        vm.$toast.error('データの取得に失敗しました')
      } finally {
        isLoading.value = false
      }
    }

    // ダイアログ操作
    const openCreateDialog = () => {
      editingItem.value = null
      showFormDialog.value = true
    }

    const openEditDialog = item => {
      editingItem.value = item
      showFormDialog.value = true
    }

    const openReplenishmentDialog = item => {
      targetItem.value = item
      showReplenishmentDialog.value = true
    }

    const openAdjustmentDialog = item => {
      targetItem.value = item
      showAdjustmentDialog.value = true
    }

    // 削除
    const confirmDelete = item => {
      deleteTargetIds.value = [item.id]
      showDeleteDialog.value = true
    }

    const confirmBulkDelete = () => {
      deleteTargetIds.value = selectedItems.value.map(item => item.id)
      showDeleteDialog.value = true
    }

    const executeDelete = async () => {
      isDeleting.value = true

      try {
        if (deleteTargetIds.value.length === 1) {
          await InventoryItemApi.destroy(deleteTargetIds.value[0])
        } else {
          await InventoryItemApi.bulkDestroy(deleteTargetIds.value)
        }

        vm.$toast.success('対象から外しました')
        showDeleteDialog.value = false
        selectedItems.value = []
        await fetchData()
      } catch (error) {
        vm.$toast.error('対象から外すことに失敗しました')
      } finally {
        isDeleting.value = false
      }
    }

    // 保存後のコールバック
    const handleSaved = () => {
      fetchData()
    }

    // 初期データ取得
    fetchData()

    return {
      // data
      isLoading,
      isDeleting,
      inventoryItems,
      vendibleTags,
      selectedItems,
      selectedTagId,
      searchQuery,
      showFormDialog,
      showReplenishmentDialog,
      showAdjustmentDialog,
      showTagTrackingDialog,
      showDeleteDialog,
      editingItem,
      targetItem,
      headers,
      tagOptions,
      filteredItems,
      availableItems,
      deleteMessage,

      // methods
      formatDate,
      getStatusColor,
      getStatusTextClass,
      getStatusLabel,
      calcServingsPerBottle,
      calcBottles,
      formatCurrentQuantity,
      openCreateDialog,
      openEditDialog,
      openReplenishmentDialog,
      openAdjustmentDialog,
      confirmDelete,
      confirmBulkDelete,
      executeDelete,
      handleSaved,

      icons: {
        mdiMagnify,
        mdiDotsVertical,
      },
    }
  },
}
</script>
